import React, { Fragment } from "react";
import { connect } from "react-redux";
import { Icon, Input, Modal, Select, Switch } from "antd";
import { compose } from "recompose";
import { withTranslation } from "react-i18next";
import { FormItem } from "@worcket/common/components/controls";
import { withForm, isLoading } from "@worcket/common/hocs";
import { useState } from "react";

import Message from "../../components/feedback/Message";
import {
  inviteCompanyUser,
  INVITE_COMPANY_USER
} from "../../store/actions/company";

import styled from "styled-components/macro";

const FormWrapper = styled.div`
  .ant-form-item {
    margin-bottom: 12px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const ConfigWrapper = styled.div`
  margin-inline: 20px;
  display: flex;
  gap: 15px;

  .highlight {
    color: ${({ theme }) => theme["@primary"]};
  }
`;

const ROLES = [
  {
    id: "admin",
    label: "catalogs:userRoles.admin"
  },
  {
    id: "operator",
    label: "catalogs:userRoles.operator"
  }
];

const huntingMessageInfo = [
  { color: "#ffd296", message: "huntingDisableMessage" },
  { color: "#eaf7af", message: "huntingEnableMessage" }
];

const createOpportunityMessageInfo = [
  { color: "#ffd296", message: "createOpportunityDisableMessage" },
  { color: "#eaf7af", message: "createOpportunityEnableMessage" }
];

const InviteUserForm = ({
  t,
  visible,
  form,
  loading,
  closeModal,
  isHeadquarter,
  onSubmit
}) => {
  const role = isHeadquarter ? "headadmin" : form.getFieldValue("role");
  const [enableHunting, setEnableHunting] = useState(true);
  const [enableCreateOpportunity, setEnableCreateOpportunity] = useState(true);
  const configInfo = huntingMessageInfo[Number(enableHunting)];
  const createOpportunityConfigInfo =
    createOpportunityMessageInfo[Number(enableCreateOpportunity)];

  return (
    <Modal
      title={t("company:users.invite")}
      visible={visible}
      onCancel={closeModal}
      onOk={onSubmit}
      okText={t("company:users.sendInvite")}
      cancelText={t("company:users.cancel")}
      confirmLoading={loading}
      width={400}
      destroyOnClose={true}
      centered
    >
      <p>{t("company:users.description")}</p>
      <FormWrapper>
        <FormItem
          form={form}
          name="name"
          label={t("company:users.name")}
          rules={[{ required: true, message: t("form:emptyFieldValidation") }]}
          render={
            <Input
              disabled={loading}
              prefix={<Icon type="user" style={{ color: "rgba(0,0,0,.25)" }} />}
            />
          }
        />
        <FormItem
          form={form}
          name="email"
          rules={[
            { required: true, message: t("form:enterEmail") },
            { type: "email", message: t("form:validEmail") }
          ]}
          label={t("company:users.email")}
          render={
            <Input
              disabled={loading}
              prefix={<Icon type="mail" style={{ color: "rgba(0,0,0,.25)" }} />}
              placeholder={t("form:placeholderEmail")}
            />
          }
        />
        {!isHeadquarter && (
          <Fragment>
            <FormItem
              form={form}
              name="role"
              label={t("company:users.role")}
              rules={[
                { required: true, message: t("form:emptyFieldValidation") }
              ]}
              render={
                <Select style={{ width: "100%" }} disabled={loading}>
                  {ROLES.map(({ id, label }) => (
                    <Select.Option key={id}>{t(label)}</Select.Option>
                  ))}
                </Select>
              }
            />
          </Fragment>
        )}
        {role === "operator" && (
          <>
            <ConfigWrapper>
              <FormItem
                form={form}
                name="config.hunting"
                decoratorProps={{
                  valuePropName: "checked",
                  initialValue: enableHunting
                }}
                render={<Switch onChange={setEnableHunting} />}
              />
              <div className={enableHunting ? "highlight" : ""}>
                {t("company:users.config.enableHunting", {
                  hunting: t("opportunity:candidates.huntingCandidatesTitle"),
                  candidatesBase: t("breadcrumbs:candidatesBase")
                })}
              </div>
            </ConfigWrapper>
            <Message type={configInfo.color} marginBottom="5px">
              {t(`company:users.config.${configInfo.message}`, {
                candidatesBase: t("breadcrumbs:candidatesBase")
              })}
            </Message>
            <ConfigWrapper>
              <FormItem
                form={form}
                name="config.createOpportunity"
                decoratorProps={{
                  valuePropName: "checked",
                  initialValue: enableCreateOpportunity
                }}
                render={<Switch onChange={setEnableCreateOpportunity} />}
              />
              <div className={enableCreateOpportunity ? "highlight" : ""}>
                {t("company:users.config.enableCreateOpportunity")}
              </div>
            </ConfigWrapper>
            <Message
              type={createOpportunityConfigInfo.color}
              marginBottom="5px"
            >
              {t(`company:users.config.${createOpportunityConfigInfo.message}`)}
            </Message>
          </>
        )}
        {role && (
          <Message type="primary-lighter">
            {t(`company:users.inviteMessage.${role}`)}
          </Message>
        )}
      </FormWrapper>
    </Modal>
  );
};

const enhacer = compose(
  withTranslation(["company", "form"]),
  connect(
    undefined,
    { inviteCompanyUser }
  ),
  isLoading(INVITE_COMPANY_USER),
  withForm({
    onSubmit: async (
      { role, ...values },
      { inviteCompanyUser, closeModal, isHeadquarter }
    ) => {
      if (values.config) {
        values.config.candidatesBase = values.config.hunting;
      }
      await inviteCompanyUser({
        ...values,
        roles: [isHeadquarter ? "headadmin" : role]
      });
      closeModal();
    }
  })
);

export default enhacer(InviteUserForm);
